import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trash2, RotateCcw, Truck, FileText, User, Wallet, Handshake } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = {
  booking: { label: "Job", plural: "Jobs", icon: Truck, cls: "bg-blue-50 text-blaze" },
  quote: { label: "Quote", plural: "Quotes", icon: FileText, cls: "bg-amber-50 text-amber-600" },
  mover: { label: "Mover", plural: "Movers", icon: User, cls: "bg-violet-50 text-violet-600" },
  pay_entry: { label: "Pay Entry", plural: "Pay Entries", icon: Wallet, cls: "bg-green-50 text-green-600" },
  partner_payment: { label: "Partner Payment", plural: "Partner Payments", icon: Handshake, cls: "bg-sky-50 text-sky-600" },
  payment: { label: "Payment", plural: "Payments", icon: Wallet, cls: "bg-emerald-50 text-emerald-600" },
};

export const TrashBin = ({ onRestored }) => {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState("");

  const load = () => {
    axios.get(`${API}/trash`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  };
  useEffect(load, []);

  const restore = async (item) => {
    setBusy(item.id);
    try {
      await axios.post(`${API}/trash/${item.id}/restore`);
      toast.success(`Restored: ${item.label}`);
      onRestored?.(item.item_type);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not restore this item.");
    } finally {
      setBusy("");
    }
  };

  const purge = async (item) => {
    if (!window.confirm(`Delete forever: ${item.label}? This CANNOT be undone.`)) return;
    setBusy(item.id);
    try {
      await axios.delete(`${API}/trash/${item.id}`);
      toast.success("Deleted forever.");
      load();
    } catch {
      toast.error("Could not delete.");
    } finally {
      setBusy("");
    }
  };

  const emptyBin = async () => {
    if (!window.confirm(`Empty the entire bin (${items.length} items)? This CANNOT be undone.`)) return;
    try {
      await axios.delete(`${API}/trash`);
      toast.success("Bin emptied.");
      load();
    } catch {
      toast.error("Could not empty bin.");
    }
  };

  if (items === null) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blaze" /></div>;
  }

  const visible = items.filter((i) => filter === "all" || i.item_type === filter);
  const counts = items.reduce((a, i) => ({ ...a, [i.item_type]: (a[i.item_type] || 0) + 1 }), {});

  return (
    <div className="mt-8" data-testid="trash-bin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold uppercase tracking-tight text-slate-900">
            Bin <span className="text-blaze">/ Trash</span>
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Deleted jobs, quotes, movers and pay records land here. Restore them anytime or delete forever.
          </p>
        </div>
        {items.length > 0 && (
          <button
            onClick={emptyBin}
            className="flex items-center gap-2 rounded-lg border border-red-300 px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-red-600 transition-all hover:bg-red-50"
            data-testid="empty-bin-btn"
          >
            <Trash2 className="h-3.5 w-3.5" /> Empty Bin
          </button>
        )}
      </div>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="trash-filters">
        <button
          onClick={() => setFilter("all")}
          className={`px-4 py-2 text-xs font-bold uppercase tracking-widest transition-all ${filter === "all" ? "bg-blaze text-white" : "border border-line text-slate-600 hover:border-blaze hover:text-blaze"}`}
          data-testid="trash-filter-all"
        >
          All ({items.length})
        </button>
        {Object.entries(TYPES).map(([k, t]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-widest transition-all ${filter === k ? "bg-blaze text-white" : "border border-line text-slate-600 hover:border-blaze hover:text-blaze"}`}
            data-testid={`trash-filter-${k}`}
          >
            {t.plural} ({counts[k] || 0})
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <div className="mt-8 flex flex-col items-center border border-dashed border-line py-20 text-center" data-testid="trash-empty">
          <Trash2 className="mb-3 h-10 w-10 text-slate-300" strokeWidth={1.4} />
          <p className="text-sm text-slate-500">The bin is empty{filter !== "all" ? " for this type" : ""}.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-3" data-testid="trash-list">
          {visible.map((item) => {
            const t = TYPES[item.item_type] || TYPES.booking;
            const Icon = t.icon;
            return (
              <div key={item.id} className="flex flex-col gap-3 rounded-xl border border-line bg-white p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`trash-item-${item.id}`}>
                <div className="flex items-center gap-3">
                  <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${t.cls}`}>
                    <Icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{item.label}</p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      <span className="mr-2 inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600">{t.label}</span>
                      Deleted {new Date(item.deleted_at).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    onClick={() => restore(item)}
                    disabled={busy === item.id}
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-white transition-all hover:bg-green-700 disabled:opacity-50 sm:flex-none"
                    data-testid={`restore-${item.id}`}
                  >
                    {busy === item.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Restore
                  </button>
                  <button
                    onClick={() => purge(item)}
                    disabled={busy === item.id}
                    className="flex items-center justify-center gap-2 rounded-lg border border-red-300 px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-red-600 transition-all hover:bg-red-50 disabled:opacity-50"
                    data-testid={`purge-${item.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete Forever
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
